import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Phone, MapPin, Star, Euro, Trash2,
  Loader2, AlertTriangle, Mails, CheckCircle2, Copy, ExternalLink,
  List, LayoutGrid, BarChart3, Mail, Send,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import SousTraitantForm from '@/components/forms/SousTraitantForm';
import { SousTraitantAIActions } from '@/components/AIActionButtons';
import SousTraitantsKanban from '@/components/SousTraitantsKanban';
import { TRADES, FALLBACK_TRADE as FALLBACK, tradeForSpec } from '@/lib/trades';

export default function SousTraitants() {
  const [sousTraitants, setSousTraitants] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [alertsByST, setAlertsByST] = useState({}); // { st_id: { expired: 1, warning: 2 } }
  const [tradeFilter, setTradeFilter] = useState(null); // null = tous, sinon une clé de TRADES
  const [scanningProspects, setScanningProspects] = useState(false);
  const [scanResult, setScanResult] = useState(null); // dialog affichant le résumé du dernier scan
  const [scanOptionsOpen, setScanOptionsOpen] = useState(false);
  const [scanUnseenOnly, setScanUnseenOnly] = useState(false); // par défaut : rescanner tout (dédup fait son travail)
  const [scanLookback, setScanLookback] = useState(60);
  const [scanAutoSend, setScanAutoSend] = useState(false); // par défaut : MANUEL, l'admin envoie ensuite depuis la fiche
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('st-view-mode') || 'list'); // 'list' | 'kanban'
  const [onboardingLinkModal, setOnboardingLinkModal] = useState(null); // { st, form_url, sent? }
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Sous-traitants - Reno Resolve';
    fetchSousTraitants();
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const r = await api.get('/sous-traitants/documents/alerts');
      const map = {};
      (r.data.alerts || []).forEach((a) => {
        if (!map[a.sous_traitant_id]) map[a.sous_traitant_id] = { expired: 0, warning: 0 };
        map[a.sous_traitant_id][a.status === 'expired' ? 'expired' : 'warning']++;
      });
      setAlertsByST(map);
    } catch (_e) {
      // silent — not critical
    }
  };

  const fetchSousTraitants = async () => {
    try {
      const response = await api.get('/sous-traitants');
      setSousTraitants(response.data);
      try {
        sessionStorage.setItem('sous_traitants_list_order',
          JSON.stringify((response.data || []).map(s => s.sous_traitant_id)));
      } catch (_) {}
    } catch (error) {
      console.error('Erreur chargement sous-traitants:', error);
      toast.error('Erreur lors du chargement des sous-traitants');
    } finally {
      setLoading(false);
    }
  };

  const handleSTCreated = async (newST) => {
    setShowForm(false);
    setTimeout(fetchSousTraitants, 0);
    toast.success('Artisan ajouté');
    // Si l'artisan a un email, on génère automatiquement le lien d'onboarding
    // (dry_run=true : token créé, email NON envoyé — l'admin choisit ensuite)
    if (newST?.sous_traitant_id && newST?.email) {
      try {
        const r = await api.post(`/sous-traitants/${newST.sous_traitant_id}/send-onboarding`, { dry_run: true });
        setOnboardingLinkModal({
          st: newST,
          form_url: r.data.form_url,
          sent: false,
        });
      } catch (e) {
        console.error('Onboarding link generation failed', e);
        // Silent — l'utilisateur peut toujours envoyer le lien depuis la fiche
      }
    }
  };

  const sendOnboardingEmail = async () => {
    if (!onboardingLinkModal?.st?.sous_traitant_id) return;
    try {
      const r = await api.post(`/sous-traitants/${onboardingLinkModal.st.sous_traitant_id}/send-onboarding`, { dry_run: false });
      setOnboardingLinkModal({
        ...onboardingLinkModal,
        sent: true,
        sent_to: r.data.sent_to,
      });
      toast.success(`Email envoyé à ${r.data.sent_to}`);
      fetchSousTraitants();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec envoi email');
    }
  };

  const copyOnboardingLink = async () => {
    if (!onboardingLinkModal?.form_url) return;
    try {
      await navigator.clipboard.writeText(onboardingLinkModal.form_url);
      toast.success('Lien copié dans le presse-papier');
    } catch (_) {
      toast.error('Impossible de copier — sélectionne le lien manuellement');
    }
  };

  const handleDelete = async (st) => {
    if (!window.confirm(`Supprimer ${st.nom} ?`)) return;
    setDeleting(st.sous_traitant_id);
    try {
      await api.delete(`/sous-traitants/${st.sous_traitant_id}`);
      toast.success('Artisan supprimé');
      fetchSousTraitants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleScanProspects = async () => {
    setScanOptionsOpen(false);
    setScanningProspects(true);
    setScanResult(null);
    try {
      const r = await api.post('/prospect/scan-imap', {
        lookback_days: scanLookback,
        unseen_only: scanUnseenOnly,
        mark_read: true,
        max_emails: 100,
        auto_send_onboarding: scanAutoSend,
        dry_run: false,
      });
      setScanResult(r.data);
      const { created = 0, onboarding_sent = 0, candidates = 0, test_email_override } = r.data || {};
      if (created > 0) {
        toast.success(
          `${created} nouveau(x) prospect(s) · ${onboarding_sent} email(s) envoyé(s)` +
          (test_email_override ? ` (test → ${test_email_override})` : '')
        );
        fetchSousTraitants();
      } else if (candidates > 0) {
        toast.info(`${candidates} candidature(s) trouvée(s), toutes déjà connues`);
      } else {
        toast.info('Aucune candidature détectée sur cette période.');
      }
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'Erreur lors du scan';
      setScanResult({ error: errMsg });
      toast.error(errMsg);
    } finally {
      setScanningProspects(false);
    }
  };

  // Liste plate d'artisans (une seule fois chacun, jamais dupliqué) — enrichis
  // avec la liste de leurs corps de métier (principal + secondaires) pour
  // permettre le filtrage et l'affichage sans duplication visuelle.
  const flatArtisans = useMemo(() => {
    const q = search.toLowerCase().trim();
    return sousTraitants
      .filter(
        (st) =>
          !q ||
          st.nom?.toLowerCase().includes(q) ||
          st.specialite?.toLowerCase().includes(q) ||
          (st.specialites_secondaires || []).some((s) => (s || '').toLowerCase().includes(q)) ||
          st.zone?.toLowerCase().includes(q),
      )
      .map((st) => {
        const allSpecs = [st.specialite, ...(st.specialites_secondaires || [])]
          .filter((s) => s && s.trim());
        const tradeKeys = Array.from(new Set(allSpecs.map((s) => tradeForSpec(s).key)));
        return { ...st, _trade_keys: tradeKeys };
      });
  }, [sousTraitants, search]);

  const totalArtisans = sousTraitants.length;
  // Compte le nombre d'artisans par corps (pour badge sur les chips filtres)
  const countByTrade = useMemo(() => {
    const m = new Map();
    TRADES.forEach((t) => m.set(t.key, 0));
    m.set(FALLBACK.key, 0);
    flatArtisans.forEach((st) => {
      st._trade_keys.forEach((k) => m.set(k, (m.get(k) || 0) + 1));
    });
    return m;
  }, [flatArtisans]);
  // Filtre: si un corps est sélectionné, on ne garde que les artisans capables
  const visibleArtisans = useMemo(() => {
    if (!tradeFilter) return flatArtisans;
    return flatArtisans.filter((st) => st._trade_keys.includes(tradeFilter));
  }, [flatArtisans, tradeFilter]);

  const renderStars = (note) => {
    if (!note) return null;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} className={`w-3 h-3 ${s <= note ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sous-traitants
          </h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">
            {totalArtisans} artisan{totalArtisans > 1 ? 's' : ''}
            {tradeFilter && ` · filtré en ${tradeFilter} (${visibleArtisans.length})`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {/* Toggle Liste / Kanban */}
          <div className="inline-flex bg-slate-100 rounded-lg p-1" data-testid="view-mode-toggle">
            <button
              type="button"
              onClick={() => { setViewMode('list'); localStorage.setItem('st-view-mode', 'list'); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="view-mode-list"
            >
              <List className="w-3.5 h-3.5" />
              Liste
            </button>
            <button
              type="button"
              onClick={() => { setViewMode('kanban'); localStorage.setItem('st-view-mode', 'kanban'); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="view-mode-kanban"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Kanban
            </button>
          </div>
          <Button
            onClick={() => setScanOptionsOpen(true)}
            disabled={scanningProspects}
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="scan-prospects-btn"
            title="Analyser la boîte mail pour importer les candidatures « Nouvelle candidature partenaire »"
          >
            {scanningProspects ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Mails className="w-4 h-4 mr-2" />
            )}
            {scanningProspects ? 'Analyse en cours…' : 'Scanner candidatures'}
          </Button>
          <Button
            onClick={() => navigate('/sous-traitants/comparateur')}
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="compare-tarifs-btn"
            title="Comparer les tarifs de vos artisans pour une même prestation"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Comparer tarifs
          </Button>
          <Button onClick={() => setShowForm(true)} data-testid="create-st-btn" className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel Artisan
          </Button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <SousTraitantsKanban sousTraitants={sousTraitants} alertsByST={alertsByST} />
      ) : (
      <>
      {/* Search */}
      <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-slate-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher par nom, spécialité ou zone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-st-input"
          />
        </div>
      </div>

      {/* Trade filter chips — click a trade to show only artisans capable in it */}
      <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-slate-200" data-testid="trade-filter-bar">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Filtrer par corps de métier</p>
          {tradeFilter && (
            <button
              type="button"
              onClick={() => setTradeFilter(null)}
              className="text-[11px] text-blue-700 hover:text-blue-900 underline"
              data-testid="clear-trade-filter"
            >
              Effacer le filtre
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setTradeFilter(null)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              tradeFilter === null
                ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
            }`}
            data-testid="trade-filter-all"
          >
            Tous
            <span className={`ml-0.5 px-1.5 py-0 rounded-full text-[10px] ${
              tradeFilter === null ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
            }`}>
              {totalArtisans}
            </span>
          </button>
          {TRADES.map((t) => {
            const count = countByTrade.get(t.key) || 0;
            const isActive = tradeFilter === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTradeFilter((prev) => (prev === t.key ? null : t.key))}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  isActive
                    ? `${t.bg} ${t.text} border-current shadow-sm ring-1 ${t.ring}`
                    : count > 0
                      ? 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                      : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'
                }`}
                data-testid={`trade-filter-${t.key}`}
                title={count === 0 ? 'Aucun artisan dans ce corps' : `${count} artisan(s) capable(s)`}
              >
                <Icon className="w-3 h-3" />
                <span className="truncate max-w-[140px]">{t.key}</span>
                {count > 0 && (
                  <span className={`ml-0.5 px-1.5 py-0 rounded-full text-[10px] font-semibold ${
                    isActive ? 'bg-white/60' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Flat list of artisans (no more grouping by trade) */}
      {visibleArtisans.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-200 p-10 text-center">
          <p className="text-sm text-slate-500">
            {tradeFilter
              ? `Aucun artisan capable en ${tradeFilter} pour le moment.`
              : search
                ? 'Aucun résultat pour cette recherche.'
                : "Aucun artisan enregistré. Cliquez sur « Nouvel Artisan » pour commencer."}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100" data-testid="st-flat-list">
          {visibleArtisans.map((st) => {
            const primary = tradeForSpec(st.specialite);
            const PrimaryIcon = primary.icon;
            const secondaryTrades = st._trade_keys.filter((k) => k !== primary.key);
            return (
              <div
                key={st.sous_traitant_id}
                className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_0.9fr_auto_auto] items-center gap-3 px-4 md:px-5 py-3 hover:bg-slate-50/70 transition-colors"
                data-testid={`st-row-${st.sous_traitant_id}`}
              >
                {/* Nom + corps de métier */}
                <button
                  onClick={() => navigate(`/sous-traitants/${st.sous_traitant_id}`)}
                  className="text-left min-w-0 flex items-center gap-3"
                >
                  <div className={`w-10 h-10 rounded-xl ${primary.bg} flex items-center justify-center ring-1 ${primary.ring} shrink-0`}>
                    <PrimaryIcon className={`w-5 h-5 ${primary.text}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                      <span className="truncate">{st.nom}</span>
                      {st.statut_recrutement === 'prospect' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 bg-purple-100 text-purple-700 border border-purple-200" title="Candidat en cours de vérification">
                          PROSPECT
                        </span>
                      )}
                      {st.statut_recrutement === 'relance' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 bg-orange-100 text-orange-700 border border-orange-200" title="Prospect relancé">
                          RELANCÉ
                        </span>
                      )}
                      {st.statut_recrutement === 'en_attente_docs' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 bg-amber-100 text-amber-700 border border-amber-200" title="Formulaire envoyé, en attente de retour">
                          EN ATTENTE
                        </span>
                      )}
                      {st.statut_recrutement === 'inactif' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 bg-slate-200 text-slate-600 border border-slate-300" title="Inactif">
                          INACTIF
                        </span>
                      )}
                      {alertsByST[st.sous_traitant_id] && (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${
                            alertsByST[st.sous_traitant_id].expired > 0
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                          title={`${alertsByST[st.sous_traitant_id].expired || 0} expiré(s), ${alertsByST[st.sous_traitant_id].warning || 0} à renouveler`}
                          data-testid={`st-alert-${st.sous_traitant_id}`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {(alertsByST[st.sous_traitant_id].expired || 0) + (alertsByST[st.sous_traitant_id].warning || 0)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${primary.bg} ${primary.text} font-medium`}>
                        {primary.key}
                      </span>
                      {secondaryTrades.slice(0, 3).map((k) => (
                        <span
                          key={k}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200"
                          title="Corps de métier secondaire"
                        >
                          + {k}
                        </span>
                      ))}
                      {secondaryTrades.length > 3 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                          +{secondaryTrades.length - 3}
                        </span>
                      )}
                      {renderStars(st.note_qualite)}
                    </div>
                  </div>
                </button>

                {/* Téléphone */}
                <div className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
                  {st.telephone && (
                    <>
                      <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{st.telephone}</span>
                    </>
                  )}
                </div>

                {/* Zone */}
                <div className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
                  {st.zone && (
                    <>
                      <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{st.zone}</span>
                    </>
                  )}
                </div>

                {/* Tarif */}
                <div className="flex items-center gap-2 text-sm">
                  {st.tarif_journalier ? (
                    <>
                      <Euro className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="font-medium text-blue-600 truncate">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(st.tarif_journalier)}/j
                      </span>
                    </>
                  ) : st.prix_range ? (
                    <span className="font-medium text-amber-600">{st.prix_range}</span>
                  ) : null}
                </div>

                {/* Actions IA */}
                <div className="flex items-center justify-end">
                  <SousTraitantAIActions sousTraitant={st} onActionComplete={fetchSousTraitants} />
                </div>

                {/* Delete */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(st)}
                  disabled={deleting === st.sous_traitant_id}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-st-${st.sous_traitant_id}`}
                  title="Supprimer cet artisan"
                >
                  {deleting === st.sous_traitant_id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Trash2 className="w-4 h-4" />}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau Sous-traitant</DialogTitle>
          </DialogHeader>
          <SousTraitantForm
            sousTraitant={null}
            onSuccess={handleSTCreated}
            onCancel={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Onboarding link dialog (after manual create) */}
      <Dialog open={!!onboardingLinkModal} onOpenChange={(open) => !open && setOnboardingLinkModal(null)}>
        <DialogContent className="max-w-lg" data-testid="onboarding-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Artisan créé — Envoyer le formulaire ?
            </DialogTitle>
          </DialogHeader>
          {onboardingLinkModal && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Artisan</div>
                <div className="font-semibold text-slate-900 mt-0.5">{onboardingLinkModal.st?.nom}</div>
                <div className="text-sm text-slate-600 mt-0.5">{onboardingLinkModal.st?.email || 'aucun email'}</div>
              </div>

              <div>
                <div className="text-xs text-slate-600 mb-2">
                  Ce lien permet à l'artisan de remplir ses documents (Kbis, assurance décennale, RIB…), ses zones d'intervention et ses prix. Il expire dans <b>30 jours</b>.
                </div>
                <div className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg p-2">
                  <input
                    readOnly
                    value={onboardingLinkModal.form_url || ''}
                    className="flex-1 bg-transparent text-xs font-mono text-slate-700 outline-none min-w-0"
                    onFocus={(e) => e.target.select()}
                    data-testid="onboarding-link-input"
                  />
                  <Button size="sm" variant="outline" onClick={copyOnboardingLink} data-testid="onboarding-link-copy">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(onboardingLinkModal.form_url, '_blank')} data-testid="onboarding-link-open">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {onboardingLinkModal.sent && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  Email envoyé à <b>{onboardingLinkModal.sent_to}</b>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setOnboardingLinkModal(null)} data-testid="onboarding-link-close">
              Fermer
            </Button>
            <Button variant="outline" onClick={copyOnboardingLink} data-testid="onboarding-link-copy-footer">
              <Copy className="w-4 h-4 mr-1.5" /> Copier le lien
            </Button>
            {!onboardingLinkModal?.sent && onboardingLinkModal?.st?.email && (
              <Button onClick={sendOnboardingEmail} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="onboarding-link-send-email">
                <Send className="w-4 h-4 mr-1.5" /> Envoyer par email
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan options dialog */}
      <Dialog open={scanOptionsOpen} onOpenChange={setScanOptionsOpen}>
        <DialogContent className="max-w-md" data-testid="scan-options-dialog">
          <DialogHeader>
            <DialogTitle>Scanner les candidatures</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Le CRM va analyser toutes vos boîtes mail configurées, détecter les
              candidatures « Nouvelle candidature partenaire… » via GPT-4o, créer les
              artisans en statut <b>prospect</b> puis leur envoyer le formulaire
              d'onboarding.
            </p>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Période d'analyse</label>
              <select
                value={scanLookback}
                onChange={(e) => setScanLookback(parseInt(e.target.value, 10))}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                data-testid="scan-lookback-select"
              >
                <option value={7}>7 derniers jours</option>
                <option value={30}>30 derniers jours</option>
                <option value={60}>60 derniers jours</option>
                <option value={180}>6 derniers mois</option>
                <option value={365}>12 derniers mois</option>
              </select>
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="unseen-only"
                checked={scanUnseenOnly}
                onChange={(e) => setScanUnseenOnly(e.target.checked)}
                className="mt-1"
                data-testid="scan-unseen-only-cb"
              />
              <label htmlFor="unseen-only" className="text-sm text-slate-700">
                <b>Emails non lus uniquement</b><br />
                <span className="text-xs text-slate-500">Décoché : rescanne aussi les emails déjà lus (la dédup empêche les doublons). Recommandé pour une première utilisation.</span>
              </label>
            </div>

            <div className="border-t border-slate-200 pt-3 mt-3">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Envoi de l'email d'onboarding</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setScanAutoSend(false)}
                  className={`px-3 py-2 rounded-lg border text-sm text-left ${!scanAutoSend ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                  data-testid="scan-mode-manual"
                >
                  <div className="font-semibold">Manuel</div>
                  <div className="text-xs opacity-70">Vous vérifierez d'abord chaque prospect, puis enverrez depuis sa fiche</div>
                </button>
                <button
                  type="button"
                  onClick={() => setScanAutoSend(true)}
                  className={`px-3 py-2 rounded-lg border text-sm text-left ${scanAutoSend ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                  data-testid="scan-mode-auto"
                >
                  <div className="font-semibold">Automatique</div>
                  <div className="text-xs opacity-70">Envoi immédiat à chaque nouvelle candidature détectée</div>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScanOptionsOpen(false)}>Annuler</Button>
            <Button onClick={handleScanProspects} data-testid="scan-launch-btn">
              <Mails className="w-4 h-4 mr-2" />
              Lancer le scan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan result dialog */}
      <Dialog open={!!scanResult} onOpenChange={(open) => !open && setScanResult(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="scan-result-dialog">
          <DialogHeader>
            <DialogTitle>Résultat du scan</DialogTitle>
          </DialogHeader>
          {scanResult?.error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{scanResult.error}</span>
            </div>
          ) : scanResult && (
            <div className="space-y-4">
              {scanResult.test_email_override && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
                  <b>Mode test :</b> tous les emails sont redirigés vers <code>{scanResult.test_email_override}</code>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatBlock label="Boîtes scannées" value={scanResult.mailboxes_scanned || 0} />
                <StatBlock label="Emails analysés" value={scanResult.scanned_emails || 0} />
                <StatBlock label="Candidatures détectées" value={scanResult.candidates || 0} accent="blue" />
                <StatBlock label="Nouveaux prospects" value={scanResult.created || 0} accent="green" />
                <StatBlock label="Doublons ignorés" value={scanResult.skipped_dup || 0} />
                <StatBlock label="Non pertinents" value={scanResult.skipped_not_prospect || 0} />
                <StatBlock label="Emails d'onboarding envoyés" value={scanResult.onboarding_sent || 0} accent="green" />
                <StatBlock label="Erreurs" value={(scanResult.errors || []).length} accent={scanResult.errors?.length ? 'red' : undefined} />
              </div>

              {(scanResult.prospects || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-2">Candidatures traitées</h3>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {scanResult.prospects.map((p, i) => (
                      <div key={i} className={`border rounded-lg p-3 flex items-center gap-3 ${p.created ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`} data-testid={`scan-prospect-${i}`}>
                        {p.created ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <Copy className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-800 truncate">{p.nom}</div>
                          <div className="text-xs text-slate-600 truncate">
                            {p.specialite} · {p.email || 'sans email'} · {p.telephone || 'sans tél.'}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {p.created && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">Créé</span>}
                          {!p.created && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-medium">Doublon</span>}
                          {p.onboarding_sent && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">Email envoyé</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(scanResult.errors || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-red-800 mb-2">Erreurs</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-900 space-y-1 max-h-32 overflow-y-auto">
                    {scanResult.errors.map((e, i) => (<div key={i}>· {e}</div>))}
                  </div>
                </div>
              )}

              {(!scanResult.candidates || scanResult.candidates === 0) && !scanResult.errors?.length && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-600">
                  Aucune candidature détectée sur cette période. Élargissez la période ou décochez « emails non lus uniquement » pour rescanner l'historique.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScanResult(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBlock({ label, value, accent }) {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    red: 'bg-red-50 border-red-200 text-red-900',
  };
  const cls = colors[accent] || 'bg-slate-50 border-slate-200 text-slate-900';
  return (
    <div className={`rounded-lg border ${cls} px-3 py-2`}>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wider opacity-70 leading-tight">{label}</div>
    </div>
  );
}
